import React, { useState, useEffect } from 'react';
import TituloDaAba, { nomeDaTelaForaDoMenu } from './TituloDaAba';
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import api from '../utils/api';
import { canAccess , podeGerir } from '../hooks/usePermission';
import UserAvatar from './common/UserAvatar';
import PerfilModal from './PerfilModal';
import NotificacoesDropdown from './NotificacoesDropdown';
import ChatWidget from './chat/ChatWidget';
import {
  LayoutDashboard, Users, Truck, UserCog, LogOut, Calendar, Building2,
  ClipboardList, FileText, Settings, MapPin, HardHat, GanttChartSquare,
  Package, FileBarChart, PanelLeftClose, PanelLeftOpen, ShoppingBag,
  AlertTriangle, TrendingUp, BarChart2, Layers,
  KanbanSquare, Boxes, MessageSquare, Sparkles, Workflow,
  CalendarClock, UserPlus, ListChecks, ClipboardCheck,
} from 'lucide-react';

const ROUTE_MODULE_MAP = {
  '/dashboard': 'dashboard',
  '/programacao': 'programacao_operacional',
  '/ordens-servico': 'ordens_servico',
  '/clientes': 'clientes',
  '/colaboradores': 'colaboradores',
  '/veiculos': 'veiculos',
  '/obras': 'obras',
  '/terceiros': 'terceiros',
  '/checklist-mobilizacao': 'checklist_mobilizacao',
  '/cronogramas': 'cronogramas',
  '/dashboard-obras': 'obras',
  '/mapa': 'mapa',
  '/materiais': 'materiais',
  '/relatorios-rnc': 'rnc',
  '/relatorios-rrm': 'rrm',
  '/relatorios': 'relatorios',
  '/rnc': 'rnc',
  '/rrm': 'rrm',
  '/backup': 'configuracoes',
  '/dashboard-mobile': 'dashboard',
  '/solicitacoes-pos-venda': 'solicitacoes_pos_venda',
  // Balcão único: acessível a QUALQUER permissão de solicitação (consistente com o item da
  // sidebar e com getDefaultRoute). Evita loop de redirect p/ usuário só-pós-venda.
  '/solicitacoes-gerais': ['solicitacoes_operacional', 'solicitacoes_novas_instalacoes', 'solicitacoes_om', 'solicitacoes_pos_venda', 'solicitacoes_suprimentos'],
  '/solicitacoes-operacional': 'solicitacoes_operacional',
  '/solicitacoes-novas-instalacoes': 'solicitacoes_novas_instalacoes',
  '/solicitacoes-om': 'solicitacoes_om',
  // `['crm','propostas']`, como as três rotas irmãs logo abaixo — e não só `'propostas'`.
  //
  // Com a chave sozinha, o vendedor que tem `crm` e não tem `propostas` (a persona das próprias
  // fixtures do repo, e um clique no editor: os dois módulos ficam em GRUPOS diferentes, cada um
  // com "marcar tudo") via o item "Propostas" na barra — porque o item usa `modulesAny` —, clicava
  // e o guarda o mandava para `/crm`, que redireciona para Negócios. Sem mensagem nenhuma.
  // Três das quatro rotas novas ganharam o par; esta ficou para trás.
  '/propostas': ['crm', 'propostas'],
  '/templates-checklist': 'templates_checklist',
  '/usuarios': 'usuarios',
  '/configuracoes': 'configuracoes',
  // CRM **ou** propostas: as telas do comercial são as duas metades do mesmo processo, e o
  // guarda tem de aceitar quem tem qualquer um dos dois. Exigir só `crm` trancaria fora
  // justamente quem hoje tem permissão de propostas — o usuário do item de menu antigo. Cada
  // tela mantém o gate dela por dentro.
  //
  // AS QUATRO ROTAS NOVAS PRECISAM ESTAR AQUI. Este mapa é o guarda de acesso por URL; rota que
  // não aparece nele cai no `else` do guarda e é tratada como livre ou como negada — e nos dois
  // casos está errado. Foi o que aconteceu quando `/crm` ficou de fora e a tela só abria pelo
  // menu.
  '/crm': ['crm', 'propostas'],
  '/leads': ['crm', 'propostas'],
  '/negocios': ['crm', 'propostas'],
  '/agenda': ['crm', 'propostas'],
  '/esteira': 'esteira',
  '/bom': 'bom',
  // `/tarefas` (o quadro do gestor) exige o modulo. `/minhas-tarefas` fica FORA deste mapa
  // de proposito: a lista e do proprio usuario, filtrada no servidor, e exigir permissao de
  // modulo para alguem ver a propria fila e o oposto do que a funcionalidade existe para
  // fazer (ver o cabecalho de `routes/tarefas.py`).
  '/tarefas': 'tarefas',
  '/estoque': 'estoque',
  '/chat': 'chat',
  '/propostas-residencial': 'propostas',
  '/assistente': 'assistente_ia',
  // Rotas de detalhe da OS (RDO/checklist) no layout admin — exigem o módulo de OS
  // (fail-closed alinhado ao resto do mapa). No app de campo o acesso é por vínculo (mobile shell).
  '/rdo': 'ordens_servico',
  '/checklist': 'ordens_servico',
};

const DashboardLayout = ({ user, onLogout, onUserUpdate }) => {
  const location = useLocation();
  const [contadores, setContadores] = useState({ novas_demandas: 0, rdos_pendentes: 0, pos_venda_abertas: 0, tarefas_abertas: 0, tarefas_atrasadas: 0, solicitacoes_abertas: {} });
  const [perfilOpen, setPerfilOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    if (saved !== null) return saved === 'true';
    // Sem preferência salva: em telas pequenas (< lg) o menu nasce FECHADO — senão o drawer
    // + overlay cobrem o conteúdo na 1ª visita. No desktop nasce expandido.
    return typeof window !== 'undefined' && window.innerWidth < 1024;
  });
  // Declarada ANTES do useEffect que a usa, e antes do `return` antecipado lá embaixo.
  //
  // Estava depois dos dois, e isso QUEBRAVA a tela: o `return` de "sem acesso à rota" (abaixo)
  // sai do componente antes de a `const` ser inicializada, mas o efeito já foi registrado —
  // hooks rodam antes de qualquer return — e dispara chamando uma `const` na temporal dead
  // zone. O ErrorBoundary pegava um `ReferenceError: Cannot access 'D' before initialization`
  // e o usuário via "Algo deu errado nesta tela" em vez de ser redirecionado.
  //
  // Quem sofria: exatamente quem tem acesso restrito (o colaborador de campo), ao abrir ou
  // receber link de uma rota que o perfil dele não cobre. Para quem tem tudo liberado, o
  // return antecipado nunca dispara e o defeito ficava invisível.
  const fetchContadores = async () => {
    try {
      const response = await api.get('/mobile/contador');
      setContadores(response.data);
    } catch (error) {}
  };

  useEffect(() => {
    fetchContadores();
    // Não faz polling com a aba oculta (economiza requisições); atualiza ao voltar.
    const interval = setInterval(() => { if (!document.hidden) fetchContadores(); }, 30000);
    const onVis = () => { if (!document.hidden) fetchContadores(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  // Verificar se o usuário tem acesso à rota atual (AFTER all hooks)
  const currentPath = location.pathname;
  // Um módulo pode ser string ou array (rota acessível se tiver QUALQUER um) — mantém o guard
  // alinhado ao item da sidebar (modulesAny), evitando negar acesso a quem a UI mostra o link.
  const routeHasAccess = (mod) => Array.isArray(mod) ? mod.some((m) => canAccess(m)) : canAccess(mod);
  const matchedModule = Object.entries(ROUTE_MODULE_MAP).find(([route]) => currentPath === route || currentPath.startsWith(route + '/'));
  if (matchedModule && !routeHasAccess(matchedModule[1])) {
    const firstAccessible = Object.entries(ROUTE_MODULE_MAP).find(([, mod]) => routeHasAccess(mod));
    if (firstAccessible) {
      return <Navigate to={firstAccessible[0]} replace />;
    }
    // NENHUM MÓDULO, MAS AINDA HÁ UMA TELA QUE É DELE: "Minhas Tarefas" não passa por permissão
    // de módulo (a lista é filtrada no servidor pelo próprio usuário). Sem este desvio, a pessoa
    // do escritório a quem o gestor acabou de atribuir uma tarefa batia em "Acesso negado" ao
    // logar e NUNCA chegava na tela feita justamente para não exigir módulo — o oposto do que o
    // dono pediu ("vai aparecer pra ele assim que logar"). Achado por céticos, 2026-08-30.
    //
    // Continua fail-CLOSED: só desvia para uma rota que não tem gate nenhum, e a tela abaixo
    // segue existindo para quem chegar nela por outro caminho. A comparação com `currentPath`
    // é o freio do laço: se um dia alguém puser `/minhas-tarefas` no mapa, o desvio pararia
    // aqui em vez de redirecionar para si mesmo para sempre.
    if (currentPath !== '/minhas-tarefas') {
      return <Navigate to="/minhas-tarefas" replace />;
    }
    // fail-CLOSED: nenhum módulo acessível → nega em vez de renderizar a tela proibida.
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-sm w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
          <h1 className="text-lg font-bold text-primary mb-2">Acesso negado</h1>
          <p className="text-sm text-slate-600 mb-6">Nenhum módulo foi liberado para o seu usuário. Procure um administrador.</p>
          <button onClick={onLogout} className="w-full py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors">
            Sair
          </button>
        </div>
      </div>
    );
  }

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const newVal = !prev;
      localStorage.setItem('sidebar_collapsed', String(newVal));
      return newVal;
    });
  };

  const sidebarWidth = sidebarCollapsed ? 'w-16' : 'w-64';
  const mainMarginClass = sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64';

  const GRUPOS_MENU = [
    {
      // Ordem ALFABÉTICA (decisão do dono, 2026-08-04) — os demais grupos seguem ordem de
      // fluxo de trabalho, este segue o nome. Item novo aqui entra na posição alfabética, não
      // no fim da lista.
      id: 'visao', label: 'Visão Geral', accentColor: '#60a5fa', icon: BarChart2,
      items: [
        { path: '/clientes', label: 'Clientes', icon: Building2, module: 'clientes' },
        { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, module: 'dashboard' },
        { path: '/esteira', label: 'Esteira do Processo', icon: Workflow, module: 'esteira' },
        // A BOLINHA E A METADE QUE FAZ A LISTA SER VISTA. O dono pediu que a tarefa aparecesse
        // "assim que logar" — sem o numero no menu, so aparece para quem ja pensou em procurar.
        // Vermelha quando ha atrasada: a cor e o que separa "tenho 3 coisas" de "tenho 3 coisas
        // e uma delas venceu".
        { path: '/minhas-tarefas', label: 'Minhas Tarefas', icon: ListChecks, livre: true,
          badge: { valor: contadores.tarefas_abertas,
                   cor: contadores.tarefas_atrasadas > 0 ? 'bg-red-500' : 'bg-blue-500' } },
        { path: '/relatorios', label: 'Relatórios', icon: FileText, module: 'relatorios' },
        { path: '/tarefas', label: 'Tarefas do Escritório', icon: ClipboardCheck, module: 'tarefas' },
      ],
    },
    {
      // Ordem ALFABÉTICA (dono, 2026-08-08): Agenda, Leads, Negócios, Propostas.
      id: 'comercial', label: 'Comercial', accentColor: '#818cf8', icon: TrendingUp,
      items: [
        // QUATRO MENUS, e não um com abas por dentro (dono, 2026-08-08): "em CRM de vendas,
        // deixará de existir e no lugar dele haverá 4 menus, Leads, Negócios, Agenda e
        // Propostas, indicadores deixar off por enquanto".
        //
        // Aba dentro de menu esconde o caminho: quem quer a agenda do dia clicava em CRM,
        // esperava o funil carregar e só então achava a aba — e não havia link direto para
        // mandar a alguém, porque a aba não estava na URL.
        //
        // `modulesAny: ['crm', 'propostas']` nos três primeiros: quem só tem permissão de
        // propostas continua vendo o comercial, como via no item antigo. Cada tela mantém o
        // gate dela por dentro.
        { path: '/agenda', label: 'Agenda', icon: CalendarClock, modulesAny: ['crm', 'propostas'] },
        { path: '/leads', label: 'Leads', icon: UserPlus, modulesAny: ['crm', 'propostas'] },
        { path: '/negocios', label: 'Negócios', icon: KanbanSquare, modulesAny: ['crm', 'propostas'] },
        { path: '/propostas', label: 'Propostas', icon: FileBarChart, modulesAny: ['crm', 'propostas'] },
        // INDICADORES fica de fora por decisão do dono ("deixar off por enquanto que futuramente
        // eu darei um novo destino a ele"). `CRMDashboard` segue no repo, intocado.
      ],
    },
    {
      // Ordem ALFABÉTICA (dono, 2026-08-08) — item novo entra na posição do nome, não no fim.
      id: 'operacional', label: 'Operacional', accentColor: '#34d399', icon: Layers,
      items: [
        { path: '/cronogramas', label: 'Cronogramas', icon: GanttChartSquare, module: 'cronogramas' },
        // Localização das pessoas: só o master vê o item (e o backend exige master no dado).
        { path: '/mapa-colaboradores', label: 'Mapa de Colaboradores', icon: Users, master: true },
        { path: '/mapa', label: 'Mapa de Demandas', icon: MapPin, module: 'mapa' },
        // OBRAS VEIO DE "Projetos & Qualidade" (dono, 2026-08-08). É onde ela sempre pertenceu:
        // toda OS aponta para uma obra, o cronograma é da obra e o custo é Cliente → Obra → OS.
        // Procurá-la num grupo de qualidade era um desvio diário.
        { path: '/obras', label: 'Obras', icon: HardHat, module: 'obras' },
        { path: '/ordens-servico', label: 'Ordens de Serviço', icon: ClipboardList, module: 'ordens_servico', badge: { valor: contadores.rdos_pendentes, cor: 'bg-purple-500' } },
        { path: '/programacao', label: 'Programação', icon: Calendar, module: 'programacao_operacional' },
        // 4 bolinhas por setor (aberta): azul=operacional, verde=novas instalações, roxo=O&M, laranja=pós-venda.
        // Só aparece a do setor com contagem > 0; cores canônicas de SolicitacoesGerais.constants (SETORES).
        { path: '/solicitacoes-gerais', label: 'Solicitações', icon: ShoppingBag, modulesAny: ['solicitacoes_operacional', 'solicitacoes_novas_instalacoes', 'solicitacoes_om', 'solicitacoes_pos_venda', 'solicitacoes_suprimentos'], badges: [
          { valor: contadores.solicitacoes_abertas?.operacional,       cor: 'bg-blue-500',   titulo: 'Operacional (abertas)' },
          { valor: contadores.solicitacoes_abertas?.novas_instalacoes, cor: 'bg-green-500',  titulo: 'Novas Instalações (abertas)' },
          { valor: contadores.solicitacoes_abertas?.om,                cor: 'bg-purple-500', titulo: 'O&M (abertas)' },
          { valor: contadores.solicitacoes_abertas?.pos_venda,         cor: 'bg-orange-500', titulo: 'Pós-Venda (abertas)' },
          { valor: contadores.solicitacoes_abertas?.suprimentos,       cor: 'bg-teal-500',   titulo: 'Suprimentos (abertas)' },
        ] },
      ],
    },
    {
      id: 'projetos', label: 'Projetos & Qualidade', accentColor: '#fbbf24', icon: HardHat,
      items: [
        // "Obras" saiu daqui para Operacional (dono, 2026-08-08).
        { path: '/terceiros', label: 'Equipes Terceiras', icon: Users, module: 'terceiros' },
        { path: '/bom', label: 'BOM / Materiais', icon: Boxes, module: 'bom' },
        { path: '/checklist-mobilizacao', label: 'Mobilização de Obra', icon: ClipboardList, module: 'checklist_mobilizacao', badge: { valor: contadores.mobilizacao_pendentes, cor: 'bg-amber-500' } },
        { path: '/relatorios-rnc', label: 'RNC', icon: AlertTriangle, module: 'rnc' },
        { path: '/relatorios-rrm', label: 'RRM', icon: Package, module: 'rrm' },
        { path: '/templates-checklist', label: 'Templates', icon: FileText, module: 'templates_checklist' },
      ],
    },
    {
      id: 'recursos', label: 'Recursos', accentColor: '#fb923c', icon: Users,
      items: [
        { path: '/colaboradores', label: 'Colaboradores', icon: Users, module: 'colaboradores' },
        { path: '/veiculos', label: 'Veículos', icon: Truck, module: 'veiculos' },
        { path: '/estoque', label: 'Estoque', icon: Boxes, module: 'estoque' },
        { path: '/materiais', label: 'Materiais', icon: Package, module: 'materiais' },
      ],
    },
  ];

  const GRUPO_SISTEMA = {
    id: 'sistema', label: 'Sistema', accentColor: '#94a3b8', icon: Settings,
    items: [
      { path: '/chat', label: 'Chat', icon: MessageSquare, module: 'chat' },
      { path: '/assistente', label: 'Assistente IA', icon: Sparkles, module: 'assistente_ia' },
      { path: '/usuarios', label: 'Usuários', icon: UserCog, module: 'usuarios' },
      { path: '/configuracoes', label: 'Configurações', icon: Settings, module: 'configuracoes' },
    ],
  };

  // `master: true` NÃO é uma permissão concedível pela tela de Usuários — vem do servidor
  // (user.is_master, calculado por deps.is_master_user). É o gate do Mapa de Colaboradores.
  const ehMaster = user?.is_master === true || user?.role === 'superadmin';
  // `livre: true` = item que NAO passa por permissao de modulo. Existe porque a tela do outro
  // lado ja e filtrada pelo proprio usuario no servidor (hoje: "Minhas Tarefas", cuja lista e
  // `atribuidos.id contem voce`), e exigir permissao para alguem ver a PROPRIA fila obrigaria a dar
  // ao escritorio inteiro a permissao que tambem deixa mexer na tarefa dos colegas.
  //
  // A FLAG PRECISA SER EXPLICITA. Item sem `module` cai em `canAccess(undefined)`, que le
  // `perms[undefined] || 'nenhum'` e devolve FALSE: omitir o campo esconderia o item de todo
  // mundo, em silencio, e a funcionalidade nao existiria para ninguem.
  const itemAcessivel = (item) => item.livre
    ? true
    : (item.master
      ? ehMaster
      : (item.modulesAny ? item.modulesAny.some(m => canAccess(m)) : canAccess(item.module)));

  const renderItem = (item) => {
    if (!itemAcessivel(item)) return null;
    const ativo = isActive(item.path);
    const badgeValor = item.badge?.valor || 0;
    return (
      <Link
        key={item.path}
        to={item.path}
        title={sidebarCollapsed ? item.label : ''}
        style={{ WebkitTapHighlightColor: 'transparent' }}
        className={`relative flex items-center gap-3 py-2 rounded-lg text-sm transition-colors outline-none focus:outline-none ${
          sidebarCollapsed ? 'justify-center mx-1.5 px-2' : 'mx-2 px-3'
        } ${
          ativo ? 'bg-primary/[0.07] text-primary font-medium' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
        }`}
      >
        {ativo && !sidebarCollapsed && (
          <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-primary" />
        )}
        <item.icon className={`w-4 h-4 flex-shrink-0 ${ativo ? 'text-primary' : 'text-slate-400'}`} />
        {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
        {badgeValor > 0 && (
          <span className={`${sidebarCollapsed ? 'absolute -top-0.5 -right-0.5' : 'ml-auto'} px-1.5 py-0.5 text-2xs font-bold rounded-full ${item.badge.cor} text-white flex-shrink-0 min-w-[18px] text-center`}>
            {badgeValor > 99 ? '99+' : badgeValor}
          </span>
        )}
        {item.badges && (item.badges.some(b => (b?.valor || 0) > 0)) && (
          sidebarCollapsed ? (
            // Recolhido (w-16): pílulas não cabem — pontos coloridos preservam a info de setor.
            <span className="absolute -top-1 -right-1 flex gap-0.5">
              {item.badges.filter(b => (b?.valor || 0) > 0).map((b, i) => (
                <span key={i} title={`${b.titulo}: ${b.valor}`} className={`w-2 h-2 rounded-full ${b.cor} ring-1 ring-white`} />
              ))}
            </span>
          ) : (
            <span className="ml-auto flex items-center gap-1 flex-shrink-0">
              {item.badges.filter(b => (b?.valor || 0) > 0).map((b, i) => (
                <span key={i} title={b.titulo} className={`px-1.5 py-0.5 text-2xs font-bold rounded-full ${b.cor} text-white min-w-[18px] text-center`}>
                  {b.valor > 99 ? '99+' : b.valor}
                </span>
              ))}
            </span>
          )
        )}
      </Link>
    );
  };

  // Seção discreta: rótulo cinza sempre visível (sem colapsar). No modo recolhido,
  // vira apenas um divisor fino entre os grupos.
  const renderGrupo = (grupo) => {
    const temItemAcessivel = grupo.items.some(itemAcessivel);
    if (!temItemAcessivel) return null;
    return (
      <div key={grupo.id} className="mb-1">
        {!sidebarCollapsed ? (
          <p className="px-4 pt-3 pb-1 text-2xs font-semibold tracking-wider text-slate-400 uppercase select-none">
            {grupo.label}
          </p>
        ) : (
          <div className="mx-3 my-1.5 border-t border-slate-200" />
        )}
        <div className="space-y-0.5">
          {grupo.items.map(renderItem)}
        </div>
      </div>
    );
  };

  // Sem isto o DONO (superadmin) aparecia rotulado como "Colaborador" no proprio app.
  const roleLabel = podeGerir(user) ? 'Administrador' : 'Colaborador';
  const itemAtivo = [...GRUPOS_MENU, GRUPO_SISTEMA].flatMap((g) => g.items).find((i) => isActive(i.path));
  const tituloPagina = itemAtivo?.label || nomeDaTelaForaDoMenu(location.pathname) || 'SOLARIC';

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {!sidebarCollapsed && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={toggleSidebar} />
      )}

      <aside className={`bg-white text-slate-700 border-r border-slate-200 ${sidebarWidth} flex flex-col fixed left-0 top-0 h-screen z-40 transition-all duration-200 ${
        sidebarCollapsed ? '-translate-x-full lg:translate-x-0' : 'translate-x-0'
      }`}>
        <div className="h-14 px-4 border-b border-slate-200 flex items-center flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <img src="/favicon.svg" alt="Solaric" className="w-8 h-8 rounded-lg flex-shrink-0" />
            {!sidebarCollapsed && (
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-slate-900 truncate leading-tight tracking-wide">SOLARIC</h2>
                <p className="text-xxs text-slate-400 leading-tight">Energia &amp; Infraestrutura</p>
              </div>
            )}
          </div>
        </div>

        <button onClick={toggleSidebar}
          className="flex items-center justify-center gap-2 mx-2 mt-2 mb-1 py-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all text-sm"
          title={sidebarCollapsed ? 'Expandir menu' : 'Minimizar menu'}>
          {sidebarCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          {!sidebarCollapsed && <span className="text-xs">Minimizar</span>}
        </button>

        <nav className="flex-1 py-2 overflow-y-auto">
          {GRUPOS_MENU.map((grupo) => renderGrupo(grupo))}
          <div className="mx-4 my-2 border-t border-slate-200" />
          {renderGrupo(GRUPO_SISTEMA)}
        </nav>

        <div className="p-3 border-t border-slate-200 flex-shrink-0">
          {!sidebarCollapsed ? (
            <div className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-slate-50 transition-colors">
              <UserAvatar user={user} size={32} onClick={() => setPerfilOpen(true)} title="Meu perfil" />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-xsm text-slate-800 truncate leading-tight">{user.name}</p>
                <p className="text-xxs text-slate-400 truncate leading-tight">{roleLabel}</p>
              </div>
              <button onClick={onLogout} title="Sair" data-testid="logout-button"
                className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button onClick={onLogout} title="Sair" data-testid="logout-button"
              className="w-full flex justify-center p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
          )}
        </div>
      </aside>

      <main className={`flex-1 min-w-0 transition-all duration-200 ${mainMarginClass} min-h-screen overflow-x-hidden`}>
        <div className="hidden lg:flex sticky top-0 z-20 h-14 bg-white border-b border-slate-200 px-5 items-center gap-4">
          <TituloDaAba titulo={tituloPagina === 'SOLARIC' ? null : tituloPagina} />
          <span className="text-sm text-slate-400 whitespace-nowrap">SOLARIC&nbsp;/&nbsp;<span className="text-slate-700 font-medium">{tituloPagina}</span></span>
          <div className="flex-1" />
          <NotificacoesDropdown />
          <UserAvatar user={user} size={36} onClick={() => setPerfilOpen(true)} title="Meu perfil" />
        </div>
        <div className="lg:hidden sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <button onClick={toggleSidebar} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex items-center gap-2">
            <UserAvatar user={user} size={32} onClick={() => setPerfilOpen(true)} title="Meu perfil" />
            <span className="text-sm font-medium text-slate-700">{user.name}</span>
          </div>
          <button onClick={onLogout} className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
        <div className="w-full"><Outlet /></div>
      </main>

      <PerfilModal open={perfilOpen} onClose={() => setPerfilOpen(false)} user={user} onUpdated={onUserUpdate} />
      {/* Chat flutuante global — visível em qualquer menu; auto-oculta em /chat. */}
      <ChatWidget />
    </div>
  );
};

export default DashboardLayout;
